// Whether onboarding runs via SAM voice or the manual form. Decided by the microphone-permission
// prompt shown right after the user picks/skips a plan, and read by the setup wizard to choose
// between the SAM welcome (voice_intro) and the plain form. Stored in sessionStorage so it survives
// the plans → setup route change (and a refresh) but resets on tab close.
export const ONBOARDING_VOICE_MODE_KEY = '__sc_onboarding_voice_mode__';

export type OnboardingVoiceMode = 'voice' | 'manual';

/**
 * Ask for microphone access (voice also needs audio playback, which any user gesture already
 * unlocks). Returns 'voice' if the grant succeeds, else 'manual'. We immediately stop the tracks —
 * we only wanted the permission, not a live capture; the real session acquires its own stream.
 */
export async function requestOnboardingVoiceMode(): Promise<OnboardingVoiceMode> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return 'manual';
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return 'voice';
  } catch {
    return 'manual'; // denied / dismissed / no device → fall back to the manual form
  }
}

export function setOnboardingVoiceMode(mode: OnboardingVoiceMode): void {
  try { sessionStorage.setItem(ONBOARDING_VOICE_MODE_KEY, mode); } catch { /* storage unavailable */ }
}

/** Defaults to voice when unset (e.g. a resumed session), matching the pre-gate behavior. */
export function isOnboardingVoiceEnabled(): boolean {
  try { return sessionStorage.getItem(ONBOARDING_VOICE_MODE_KEY) !== 'manual'; } catch { return true; }
}
